import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { NJInlineMessage, NJText } from '@engie-group/fluid-design-system-react';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import { Toast } from './ui';
import { CreateWizard } from './CreateWizard';
import { HomePage } from './HomePage';
import { useProjects, useReferential } from './hooks';
import { ProjectView } from './ProjectView';
import { ConfigurationWireframes } from './ConfigurationWireframes';
import { PlansWireframes } from './PlansWireframes';
import { STUDIO_HEADER, UMR } from './tokens';
import { readTestTerritory } from './testTerritory';
import { TestTerritorySetup } from './TestTerritorySetup';

export const meta = {
  title: 'Plan navigation V3 - Revue',
  description:
    'Navigation Plan sur huit communes : géométries publiques, attributs métier et zones météo fictifs.',
  tags: ['wizard', 'planning', 'map'],
  author: 'KR4259_engieco, DV5886',
  status: 'draft' as const,
  createdAt: '2026-11-02',
  updatedAt: '2026-11-02',
};

type View = { kind: 'home'; wizard: boolean } | { kind: 'project'; id: string };

/** `?projets=0` dans l'URL affiche l'état vide de l'accueil. */
const emptyHome = () => new URLSearchParams(window.location.search).get('projets') === '0';

export default function PlanNavigationV3() {
  const search = new URLSearchParams(window.location.search);
  if (search.get('view') === 'wireframes') {
    return search.get('review') === 'plans' ? <PlansWireframes /> : <ConfigurationWireframes />;
  }
  const selection = readTestTerritory();
  if (!selection.codes.length || search.get('configuration') === '1') {
    return <TestTerritorySetup title={meta.title} initialCodes={selection.codes} error={selection.error} />;
  }
  return <PlanTestSession key={selection.codes.join(',')} />;
}

function PlanTestSession() {
  const qc = useQueryClient();
  const [empty] = useState(emptyHome);
  const projects = useProjects(empty);
  const referential = useReferential();
  const [view, setView] = useState<View>({ kind: 'home', wizard: false });
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (document.querySelector(`link[href="${UMR.fontHref}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = UMR.fontHref;
    document.head.appendChild(link);
  }, []);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const flash = useCallback((msg: string) => {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3200);
  }, []);

  const goHome = () => {
    setView({ kind: 'home', wizard: false });
    void qc.invalidateQueries({ queryKey: ['plan2cost', 'plan-navigation-v3'] });
  };

  let body: JSX.Element;
  if (view.kind === 'project') {
    body = referential.data
      ? <ProjectView key={view.id} projectId={view.id} referential={referential.data} onBack={goHome} flash={flash} />
      : <div style={{ padding: 24 }}>{referential.isError ? 'Erreur de chargement du référentiel.' : 'Chargement…'}</div>;
  } else {
    body = (
      <div style={{ position: 'relative', height: '100%' }}>
        {(!view.wizard || !referential.data) && <HomePage
          projects={projects.data}
          loading={projects.isLoading}
          error={projects.isError}
          onCreate={() => setView({ kind: 'home', wizard: true })}
          onOpen={(id) => setView({ kind: 'project', id })}
          flash={flash}
        />}
        {view.wizard && referential.data && (
          <CreateWizard
            referential={referential.data}
            flash={flash}
            onCancel={() => setView({ kind: 'home', wizard: false })}
            onOpen={(id) => {
              setView({ kind: 'project', id });
              flash('Projet créé · copie des bâtiments de démonstration figée à aujourd’hui');
            }}
          />
        )}
      </div>
    );
  }

  return (
    <PrototypeStudioLayout title={meta.title} commentScope={view.kind === 'project' ? `project-${view.id}` : view.wizard ? 'creation' : undefined}>
      <div data-comment-view={view.kind === 'project' ? `project-${view.id}` : view.wizard ? 'creation' : 'home'} style={{ position: 'relative', display: 'flex', flexDirection: 'column', height: `calc(100vh - ${STUDIO_HEADER}px)`, overflow: 'hidden', fontFamily: UMR.font, background: UMR.white, color: UMR.textStrong }}>
        <div style={{ flex: 1, minHeight: 0 }}>
          {referential.isError
            ? <NJInlineMessage variant="error" scale="sm" role="alert">
              <NJText>{referential.error instanceof Error ? referential.error.message : 'Données publiques de démonstration indisponibles.'}</NJText>
            </NJInlineMessage>
            : body}
        </div>
        {toast && <Toast>{toast}</Toast>}
      </div>
    </PrototypeStudioLayout>
  );
}
